/**
 * useSSE — Vue composable for Server-Sent Events over fetch.
 *
 * Uses fetch + eventsource-parser instead of native EventSource so that
 * custom headers (Authorization, Last-Event-ID) can be sent. Mirrors the
 * reactive interface of useWebSocket (status, isConnected, isFetching, etc.).
 *
 * Features:
 *  - JWT auth with automatic 401 → token-refresh → retry (same as api.ts)
 *  - Reconnection with exponential back-off and jitter
 *  - Server-sent `retry:` field support
 *  - Inactivity timeout to detect silent connection drops
 *  - Last-Event-ID tracking for resumable streams
 *  - Typed event handler registration (onEvent / offEvent)
 *  - Automatic cleanup on component unmount
 */
import { computed, onScopeDispose, ref } from 'vue';

import type { AuthSession } from '@aws-amplify/auth';
import * as Sentry from '@sentry/vue';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { EventSourceMessage } from 'eventsource-parser/stream';
import { EventSourceParserStream } from 'eventsource-parser/stream';

import { logAppError } from '@/config/sentryConfig';
import router from '@/router';
import { getSafeAbortSignal, isLogoutInProgress } from '@/services/api';
import { logout, LogoutReason } from '@/services/auth/logoutService';
import { sseRegistry } from '@/services/auth/sseRegistry';
import { refreshAuthSession } from '@/services/auth/tokenRefresh';
import useAuthStore from '@/stores/auth';
import type {
  SSEConnectOptions,
  SSEMessage,
  SSEOptions,
  SSEStatus
} from '@/types/api/sse';

/**
 * Sentinel error thrown during intentional logout/sign-out.
 * Allows the composable to distinguish expected logouts from unexpected failures
 * so they don't create noise in logs or error states.
 */
class IntentionalLogoutError extends Error {
  constructor(message: string = 'Intentional logout') {
    super(message);
    this.name = 'IntentionalLogoutError';
  }
}

export const useSSE = (options: SSEOptions = {}) => {
  const config = {
    withAuth: true,
    headers: {} as Record<string, string>,
    reconnect: false,
    reconnectDelay: 3_000,
    maxReconnectDelay: 30_000,
    maxReconnectAttempts: 5,
    inactivityTimeout: 0,
    ...options
  };

  // ---- Reactive state (exposed) -------------------------------------------

  const status = ref<SSEStatus>('CLOSED');
  const error = ref<string | null>(null);
  const lastActivity = ref<Date | null>(null);
  const hasReceivedMessage = ref(false);
  const lastEventId = ref<string | null>(null);

  const isConnected = computed(() => status.value === 'OPEN');
  const isFetching = computed(
    () =>
      status.value === 'CONNECTING' ||
      (status.value === 'OPEN' && !hasReceivedMessage.value)
  );
  const isError = computed(() => status.value === 'ERROR');

  // ---- Internal state -----------------------------------------------------

  let abortController: AbortController | null = null;
  let reconnectAttempts = 0;
  let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let inactivityTimerId: ReturnType<typeof setTimeout> | null = null;
  let isIntentionalDisconnect = false;
  let currentUrl = '';
  let currentConnectOptions: SSEConnectOptions = {};

  // Each connect() call gets a unique id so stale read-loops can self-exit
  // when a newer connection replaces them.
  let connectionId = 0;

  let serverRetryMs: number | null = null;

  // Track if we've already attempted a token refresh for this connection's
  // opaqueredirect (302→Cognito). Allows one refresh+retry before forcing logout.
  let hasAttemptedRefreshForOpaqueredirect = false;

  const eventHandlers = new Map<
    string,
    Set<(data: string, message: SSEMessage) => void>
  >();

  // ---- Auth ---------------------------------------------------------------

  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    try {
      let session: AuthSession = await fetchAuthSession();
      let token: string | undefined = session.tokens?.idToken?.toString();

      // Missing token (not expired — simply absent) is the reported SSE failure
      // mode (SESC-13201 / BE SESC-13206): the session isn't hydrated yet when
      // the stream starts, so the request would go out with no Bearer and Bobby
      // 302s it to Cognito. Attempt one refresh to materialise the token before
      // giving up, mirroring the 401 recovery path.
      if (!token) {
        session = await refreshAuthSession();
        token = session.tokens?.idToken?.toString();
      }

      if (!token) throw new Error('No authentication token available');

      return { Authorization: `Bearer ${token}` };
    } catch (authError) {
      logAppError({
        error: authError,
        functionName: 'useSSE',
        section: 'getAuthHeaders',
        metadata: { url: currentUrl }
      });
      throw authError;
    }
  };

  // ---- Fetch with 401 retry ----------------------------------------------

  const fetchWithAuth = async (
    url: string,
    connectOptions: SSEConnectOptions = {}
  ): Promise<Response> => {
    // Capture the signal early using the shared safe getter to prevent null reference
    // if abortController is set to null by disconnectAll() during logout while this
    // function is mid-flight. Also checks if already aborted before starting any fetch.
    const signal = getSafeAbortSignal(abortController);
    if (!signal) {
      throw new Error('Connection aborted');
    }

    const method = connectOptions.method ?? 'GET';
    const body =
      connectOptions.body === undefined
        ? null
        : JSON.stringify(connectOptions.body);

    const buildHeaders = (
      authHeaders: Record<string, string>
    ): Record<string, string> => ({
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders,
      ...config.headers,
      ...(lastEventId.value && reconnectAttempts > 0
        ? { 'Last-Event-ID': lastEventId.value }
        : {})
    });

    const forceSignOut = async () => {
      try {
        await logout(undefined, LogoutReason.SessionExpired);
      } catch (logoutError) {
        // Logout should complete successfully. This catch handles unexpected errors.
        logAppError({
          error: logoutError,
          functionName: 'useSSE',
          section: 'forceSignOut',
          metadata: { context: 'unexpected_logout_error' }
        });
      }
      // Throw sentinel error so connect()'s catch block knows this is an expected
      // logout, not a failure that should log noise or set error state
      throw new IntentionalLogoutError('Session expired (forced logout)');
    };

    const authHeaders = config.withAuth ? await getAuthHeaders() : {};
    let response = await fetch(url, {
      headers: buildHeaders(authHeaders),
      signal,
      cache: 'no-store',
      // Prevent the browser from following a backend 302→Cognito redirect
      // cross-origin, which would cause a CORS error on the Cognito endpoint.
      redirect: 'manual',
      method,
      body
    });

    // Backend redirected to OAuth login without returning 401 (Spring Security
    // default when text/event-stream Accept header is sent). Try to recover
    // via token refresh + retry before forcing logout (same as 401 path).
    if (response.type === 'opaqueredirect') {
      if (!hasAttemptedRefreshForOpaqueredirect && config.withAuth) {
        hasAttemptedRefreshForOpaqueredirect = true;
        try {
          await refreshAuthSession();
          const freshAuthHeaders = await getAuthHeaders();

          // Defensive check: if the signal was aborted during refresh/auth-fetch,
          // throw AbortError so it's handled appropriately by the outer catch block.
          if (signal.aborted) {
            const err = new Error('Connection aborted during token refresh');
            err.name = 'AbortError';
            throw err;
          }

          // Retry the fetch with the fresh token
          response = await fetch(url, {
            headers: buildHeaders(freshAuthHeaders),
            signal,
            cache: 'no-store',
            redirect: 'manual',
            method,
            body
          });

          // If still opaqueredirect after refresh, the session is genuinely dead
          if (response.type === 'opaqueredirect') {
            await forceSignOut();
          }
        } catch (refreshError) {
          // AbortError (intentional disconnect or timeout) should not trigger logout
          if (
            refreshError instanceof Error &&
            refreshError.name === 'AbortError'
          ) {
            throw refreshError;
          }

          logAppError({
            error: refreshError,
            functionName: 'useSSE',
            section: 'opaqueredirect_tokenRefresh',
            metadata: { url }
          });

          // Token refresh failed, session is unrecoverable
          const isUnguarded =
            router.currentRoute.value.meta?.unguarded === true;
          const isAuthenticated = useAuthStore().isUserAuthenticated;

          if (!isUnguarded || isAuthenticated) {
            await forceSignOut();
          }

          throw refreshError;
        }
      } else {
        // Already tried refresh or no auth configured, force logout
        await forceSignOut();
      }
    }

    // 401 → refresh token once, then retry
    if (response.status === 401 && config.withAuth) {
      try {
        await refreshAuthSession();
        const freshAuthHeaders = await getAuthHeaders();

        // Defensive check: if the signal was aborted during refresh/auth-fetch,
        // throw AbortError so it's handled appropriately by the outer catch block
        // (distinguishes intentional disconnects from real auth failures).
        // Without this, an intentional abort would be caught by refreshError and
        // incorrectly trigger forceSignOut(), logging the user out unexpectedly.
        if (signal.aborted) {
          const err = new Error('Connection aborted during token refresh');
          err.name = 'AbortError';
          throw err;
        }

        response = await fetch(url, {
          headers: buildHeaders(freshAuthHeaders),
          signal,
          cache: 'no-store',
          redirect: 'manual',
          method,
          body
        });

        // If opaqueredirect after 401 refresh, force logout
        // (we've already attempted one refresh cycle)
        if (response.type === 'opaqueredirect') {
          await forceSignOut();
        }
      } catch (refreshError) {
        // AbortError (intentional disconnect or inactivity timeout) should not
        // trigger logout. Let the outer catch block handle it using isIntentionalDisconnect.
        if (
          refreshError instanceof Error &&
          refreshError.name === 'AbortError'
        ) {
          throw refreshError;
        }

        logAppError({
          error: refreshError,
          functionName: 'useSSE',
          section: 'tokenRefresh',
          metadata: { url }
        });

        // Only sign out and redirect when the current route requires authentication,
        // or when the user had an active session that has now expired.
        // Unauthenticated users on unguarded routes must not be interrupted
        // by a background SSE failure due to no session.
        const isUnguarded = router.currentRoute.value.meta?.unguarded === true;
        const isAuthenticated = useAuthStore().isUserAuthenticated;

        if (!isUnguarded || isAuthenticated) {
          // forceSignOut throws IntentionalLogoutError sentinel, which connect()'s
          // catch block will handle silently. This approach maintains clean logout flow
          // without error state or unnecessary logs.
          await forceSignOut();
        }

        throw refreshError;
      }
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
  };

  // ---- Event mapping & dispatch -------------------------------------------

  const toSSEMessage = (raw: EventSourceMessage): SSEMessage => {
    const message: SSEMessage = {
      event: raw.event ?? 'message',
      data: raw.data
    };

    if (raw.id !== undefined) message.id = raw.id;

    return message;
  };

  const dispatchEvent = (message: SSEMessage) => {
    const handlers = eventHandlers.get(message.event);
    if (!handlers || handlers.size === 0) return;

    handlers.forEach((handler) => {
      try {
        handler(message.data, message);
      } catch (handlerError) {
        logAppError({
          error: handlerError,
          functionName: 'useSSE',
          section: 'eventHandler',
          metadata: { event: message.event, url: currentUrl }
        });
      }
    });
  };

  // ---- Inactivity timeout -------------------------------------------------
  // Catches silent connection drops where TCP stays open but no data arrives.

  const clearInactivityTimer = () => {
    if (inactivityTimerId !== null) {
      clearTimeout(inactivityTimerId);
      inactivityTimerId = null;
    }
  };

  const resetInactivityTimer = () => {
    clearInactivityTimer();
    if (config.inactivityTimeout <= 0) return;

    inactivityTimerId = setTimeout(() => {
      // Expected behaviour (device sleep, backgrounded tab, idle proxy) — a
      // breadcrumb gives context on real errors without being an event itself.
      Sentry.addBreadcrumb({
        category: 'sse',
        message: 'SSE inactivity timeout, reconnecting',
        level: 'info',
        data: { url: currentUrl, timeoutMs: config.inactivityTimeout }
      });

      // Abort current connection — the read-loop catch block will trigger reconnect
      abortController?.abort();
    }, config.inactivityTimeout);
  };

  // ---- Reconnection -------------------------------------------------------

  const clearReconnectTimeout = () => {
    if (reconnectTimeoutId !== null) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
  };

  /** Exponential back-off: base * 2^attempt, capped, with ±25 % jitter. */
  const getReconnectDelay = (): number => {
    const base = serverRetryMs ?? config.reconnectDelay;
    const exponential = base * Math.pow(2, reconnectAttempts);
    const capped = Math.min(exponential, config.maxReconnectDelay);
    const jitter = capped * (0.75 + Math.random() * 0.5);
    return Math.round(jitter);
  };

  const handleStreamEnd = (wasError: boolean) => {
    // Don't reconnect if disconnect was intentional or logout is in progress
    if (isIntentionalDisconnect || isLogoutInProgress()) return;

    if (
      !config.reconnect ||
      !wasError ||
      reconnectAttempts >= config.maxReconnectAttempts
    ) {
      if (
        wasError &&
        config.reconnect &&
        reconnectAttempts >= config.maxReconnectAttempts
      ) {
        error.value = 'Connection lost. Max reconnection attempts reached.';
        status.value = 'ERROR';

        // Exhaustion while online is the genuinely bad case — unlike the
        // individual attempts (warnings), this one is an error-level event.
        // Offline exhaustion is the user's connection, not the app: warning.
        logAppError({
          error: new Error(error.value),
          functionName: 'useSSE',
          section: 'reconnect',
          ...(navigator.onLine ? {} : { level: 'warning' as const }),
          metadata: {
            url: currentUrl,
            attempts: reconnectAttempts,
            isOnline: navigator.onLine,
            visibility: document.visibilityState
          }
        });
      }
      return;
    }

    const delay = getReconnectDelay();
    reconnectAttempts++;
    status.value = 'CONNECTING';

    reconnectTimeoutId = setTimeout(() => {
      connect(currentUrl, currentConnectOptions);
    }, delay);
  };

  // ---- Public API ---------------------------------------------------------

  const connect = async (
    url: string,
    connectOptions: SSEConnectOptions = {}
  ): Promise<void> => {
    // Tear down any existing connection
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    clearReconnectTimeout();
    clearInactivityTimer();

    const thisConnectionId = ++connectionId;
    isIntentionalDisconnect = false;
    hasAttemptedRefreshForOpaqueredirect = false;

    if (url !== currentUrl) lastEventId.value = null;

    currentUrl = url;
    currentConnectOptions = connectOptions;
    status.value = 'CONNECTING';
    error.value = null;
    hasReceivedMessage.value = false;
    abortController = new AbortController();

    try {
      const response = await fetchWithAuth(url, connectOptions);

      // A newer connect() call replaced us — bail out silently
      if (thisConnectionId !== connectionId) return;

      if (!response.body) {
        throw new Error('Response body is null — streaming not supported');
      }

      status.value = 'OPEN';
      lastActivity.value = new Date();
      reconnectAttempts = 0;
      resetInactivityTimer();

      const stream = response.body
        .pipeThrough(new TextDecoderStream())
        .pipeThrough(
          new EventSourceParserStream({
            onRetry(interval) {
              serverRetryMs = interval;
            },
            onError(parseError) {
              logAppError({
                error: parseError,
                functionName: 'useSSE',
                section: 'parser',
                level: 'warning',
                metadata: { url: currentUrl }
              });
            }
          })
        );

      const reader = stream.getReader();

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done || thisConnectionId !== connectionId) break;

          lastActivity.value = new Date();
          hasReceivedMessage.value = true;
          resetInactivityTimer();

          const message = toSSEMessage(value);

          if (message.id) {
            lastEventId.value = message.id;
          }

          dispatchEvent(message);
        }

        // Stream ended normally
        if (thisConnectionId === connectionId) {
          status.value = 'CLOSED';
          handleStreamEnd(false);
        }
      } finally {
        reader.releaseLock();
      }
    } catch (err: unknown) {
      // Stale connection — a newer connect() took over
      if (thisConnectionId !== connectionId) return;

      // Intentional logout (expected flow) — close silently without error state or logs
      if (err instanceof IntentionalLogoutError) {
        status.value = 'CLOSED';
        return;
      }

      if (err instanceof Error && err.name === 'AbortError') {
        // AbortError from inactivity timeout should trigger reconnect
        if (!isIntentionalDisconnect && config.reconnect) {
          status.value = 'CONNECTING';
          handleStreamEnd(true);
          return;
        }
        status.value = 'CLOSED';
        return;
      }

      const message = err instanceof Error ? err.message : 'Unknown SSE error';
      error.value = message;
      status.value = 'ERROR';

      // Capture at the default error level and let beforeSend downgrade the
      // transient network cases (weak signal, sleep, unload) to warning — or
      // drop them when offline. A genuine failure here (e.g. streaming
      // unsupported) stays an error; exhaustion is logged separately.
      logAppError({
        error: err,
        functionName: 'useSSE',
        section: 'connect',
        metadata: {
          url,
          isOnline: navigator.onLine,
          visibility: document.visibilityState
        }
      });

      handleStreamEnd(true);
    }
  };

  const disconnect = () => {
    // Mark disconnect as intentional to prevent reconnection attempts.
    // Safe to call multiple times — idempotent operations only.
    isIntentionalDisconnect = true;
    clearReconnectTimeout();
    clearInactivityTimer();

    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    status.value = 'CLOSED';
    error.value = null; // Clear any residual error state
  };

  const onEvent = (
    eventType: string,
    handler: (data: string, message: SSEMessage) => void
  ) => {
    if (!eventHandlers.has(eventType)) {
      eventHandlers.set(eventType, new Set());
    }
    eventHandlers.get(eventType)!.add(handler);

    return () => offEvent(eventType, handler);
  };

  const offEvent = (
    eventType: string,
    handler?: (data: string, message: SSEMessage) => void
  ) => {
    if (!handler) {
      eventHandlers.delete(eventType);
      return;
    }
    const handlers = eventHandlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) eventHandlers.delete(eventType);
    }
  };

  // ---- Lifecycle ----------------------------------------------------------

  const unregister = sseRegistry.register(disconnect);

  onScopeDispose(() => {
    disconnect();
    unregister();
  });

  return {
    // State
    status,
    isConnected,
    isFetching,
    isError,
    error,
    lastActivity,
    hasReceivedMessage,
    lastEventId,

    // Methods
    connect,
    disconnect,
    onEvent,
    offEvent
  };
};
